import { Component, OnInit, EventEmitter, Output } from '@angular/core';
import { Router } from '@angular/router';
import { SaleFormService } from '@app/features/sales/add-sale/steps/sale-form.service';
import { WarrantyActivationFormService } from '@app/features/warranties/activate-warranty-process/steps/warranty-activation-form.service';
import { WarrantyActivationMethod } from '@app/shared/enums/WarrantyActivationMethod.enum';
import { Actions } from '@src/app/shared/backend/auth/Action';
import { Features } from '@src/app/shared/backend/auth/Feature';
import { CompanyResponse, GenericIntegerResponse, SalesService, SurveysService } from '@src/app/shared/backend/clients';
import { PermissionService } from '@src/app/shared/services/permission.service';
import { SessionService } from '@src/app/shared/services/session.service';
import { firstValueFrom } from 'rxjs';

interface SaleData {
  warrantyCodeData: {
    warrantyCode: string;
  };
  identifier: {
    item: any;
    date: any;
    editionNumber: any;
  };
  saleData: {
    item: any;
    itemId: string;
    retailer: any;
    retailerId: string;
    isLegacy: boolean;
    date: string;
    price: number;
    editionNumber: any;
  };
  sellerData: {
    retailer: any;
    retailerId: string;
    unregisteredUser: any;
    unregisteredUserId: string;
    user: any;
    userId: string;
    distributionChannel: any;
    distributionChannelId: string;
  };
  customerData: {
    customer?: any;
    customerId?: string | number | null;
    customerName?: string | null;
  };
}

@Component({
    selector: 'app-sale-summary',
    templateUrl: './sale-summary.component.html',
    styleUrl: './sale-summary.component.scss',
    host: {
        class: 'app-sale-summary'
    },
    standalone: false
})
export class StepSaleSummaryComponent implements OnInit {

  @Output() disablePrevBtn = new EventEmitter<void>();

  saleType = WarrantyActivationMethod;
  selectedSaleType: any;

  price: any;
  previewImageUrl: string | null = null;
  currentDate: Date = new Date();
  maxDate: Date = new Date();
  minDate: Date = new Date();
  isCalendarEnabled: boolean = true;
  saleId: number | null = null;
  showPostSaleBlock: boolean = false;
  showDistributionChannel: boolean = false;
  canModifyPrice: boolean = false;
  canViewPrice: boolean = false;
  warrantyCode: any;
  allowActivateWarranty: boolean = false;
  isGift: any = 'false';

  canTakeSurvey: boolean = false;
  postSaleSurveyId: number | null = null;

  saleFormData: SaleData = {
    warrantyCodeData: { warrantyCode: '' },
    identifier: {
      item: null,
      date: null,
      editionNumber: null
    },
    saleData: {} as any,
    sellerData: {} as any,
    customerData: {} as any
  };

  onActivateWarranty: boolean = false;
  saleCompletedSuccessfully: boolean = true;
  company: CompanyResponse = {
    inventorySubmissionThresholdDays: 0
  };

  constructor(
    private router: Router,
    private saleFormService: SaleFormService,
    private salesService: SalesService,
    private surveysService: SurveysService,
    private session: SessionService,
    private permissionService: PermissionService,
    private warrantyActivationFormService: WarrantyActivationFormService
  ) { }

  async ngOnInit() {

    this.company = await this.session.getCompanyData();
    this.saleFormData = this.saleFormService.getAllData();
    this.showDistributionChannel = await this.permissionService.companyHasFeaturePermissions(Features.DistributionChannels);
    const selectedItem = this.saleFormData.identifier?.item;
    this.previewImageUrl = selectedItem?.productCustomisation?.imageUrl || selectedItem?.product?.imageUrl || selectedItem?.product?.productType?.imageUrl || null;

    this.canViewPrice = this.company.hasPrice ?? false;
    this.price = this.saleFormData.identifier?.item?.productCustomisation?.price ?? this.saleFormData.identifier?.item?.product?.price;

    this.selectedSaleType = this.saleFormService.getStepData('saleType')?.saleType || null;
    if (this.company.isPrepaired) {
      this.warrantyCode = this.saleFormData.identifier?.item?.warranties?.find((x: { code: string; isPrepaired: boolean }) => x.isPrepaired)?.code;
    }
    else {
      this.warrantyCode = this.saleFormData.warrantyCodeData?.warrantyCode;
    }
    console.log('all data', this.saleFormData, this.company);

    await this.getCanModifyPrice();
    await this.setMinDate();

    this.allowActivateWarranty = await this.permissionService.hasActionPermissions(Actions.activate_warranties);

    // Surveys are coupled to Customer feature!
    const hasCustomerFeature = await this.permissionService.hasFeaturePermissions(Features.Customers);
    if (hasCustomerFeature) {
      await this.getPostSaleSurvey();
    }
  }

  private async getPostSaleSurvey(): Promise<void> {
    const postSaleSurveyResponse: GenericIntegerResponse = await firstValueFrom(this.surveysService.apiSurveysPostSaleGet());

    this.postSaleSurveyId = postSaleSurveyResponse?.response ?? null;
    this.canTakeSurvey = false;

    if (!this.postSaleSurveyId) {
      return;
    }

    const survey = await firstValueFrom(this.surveysService.apiSurveysIdGet(this.postSaleSurveyId));
    // console.log('post sale survey', survey);

    this.canTakeSurvey = !!survey?.isActive && !!survey?.isPublished;
  }

  async setMinDate() {
    const months = (await firstValueFrom(this.salesService.apiSalesSaleMonthsRetailerIdGet(parseInt(this.saleFormData.sellerData?.retailerId, 10)))).response || 0
    if (months > 0) {
      this.minDate.setMonth(this.maxDate.getMonth() - months);
    }
    else if (months == -1) {
      this.minDate = new Date(0)
    }
    else {
      this.isCalendarEnabled = false;
    }
  }

  async onCreteSale() {
    const now = new Date();
    if (this.currentDate.getDay() === now.getDay()) {
      this.currentDate.setHours(now.getHours(), now.getMinutes(), now.getSeconds());
    }
    let payload: any = {
      itemId: parseInt(this.saleFormData.identifier?.item?.id, 10),
      productCustomisationId: parseInt(this.saleFormData.identifier?.item?.productCustomisationId, 10),
      retailerId: parseInt(this.saleFormData.sellerData?.retailerId, 10) || parseInt(this.saleFormData.sellerData?.retailer?.id, 10),
      unregisteredUserId: parseInt(this.saleFormData.sellerData?.unregisteredUserId, 10) || parseInt(this.saleFormData.sellerData?.unregisteredUser?.id, 10) || null,
      userId: this.saleFormData.sellerData?.userId || this.saleFormData.sellerData?.user?.id || null,
      distributionChannelId: parseInt(this.saleFormData.sellerData?.distributionChannelId, 10) || parseInt(this.saleFormData.sellerData?.distributionChannel?.id, 10) || null,
      // isLegacy: this.saleFormData.saleData?.isLegacy,
      date: this.currentDate,
      price: this.price,
      customerId: this.toNumberOrNull(
        this.saleFormData.customerData?.customer?.id ?? this.saleFormData.customerData?.customerId
      ),
      customer: this.saleFormData.customerData?.customer ?? null,
      isGift: this.isGift === 'true'
    };

    // override itemId (limited editions)
    if (this.saleFormData.identifier?.item?.product?.isLimitedEdition) {
      payload.itemId = parseInt(this.saleFormData.identifier.editionNumber?.id ?? this.saleFormData.identifier.item?.id, 10);
    }

    try {
      let saleId = null;
      if (this.company.implicitSaleCreation) {
        payload.warrantyCode = this.saleFormData.warrantyCodeData?.warrantyCode?.toUpperCase();
        saleId = await firstValueFrom(this.salesService.apiSalesCreateWithActivationPost(payload));
      }
      else {
        saleId = await firstValueFrom(this.salesService.apiSalesCreatePost(payload));
      }

      this.saleId = saleId;
      this.showPostSaleBlock = true;
      this.saleCompletedSuccessfully = true;
      this.disablePrevBtn.emit();
    } catch (error) {
      console.error('Error creating sale:', error);
      this.showPostSaleBlock = false;
      this.saleCompletedSuccessfully = false;
    }
  }

  private toNumberOrNull(value: string | number | null | undefined): number | null {
    // normalize payload for these cases:
    // dropdown -> array of ids: (string | number)[]
    // restored step data -> string, number, null, or undefined
    // newly created entities -> object with id: number

    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsedValue = Number(value);

    return Number.isNaN(parsedValue) ? null : parsedValue;
  }

  goToActivateWarranty() {
    this.prefillWarrantyData();
    this.router.navigate(
      ['/warranties/activate'], { queryParams: { saleId: this.saleId } }
    );
  }

  prefillWarrantyData() {
    this.warrantyActivationFormService.setStepData('retailerId', this.saleFormData.sellerData['retailer'].id);

    this.warrantyActivationFormService.setStepData('activationMethod', { activationMethod: this.saleFormData.identifier['item']['serialNumber'] ? 1 : 2 });// 1: SN, 2: SKU

    const item = this.saleFormData.identifier['item'];
    this.warrantyActivationFormService.setStepData('itemData', item);

    // console.log('prefillWarrantyData', this.warrantyActivationFormService.getAllData() );
  }

  goToSale() {
    this.router.navigate([`/sales/${this.saleId}`]);
  }

  goToSurvey() {
    if (!this.postSaleSurveyId) {
      return;
    }

    this.router.navigate([`/surveys/${this.postSaleSurveyId}/take-survey`]);
  }

  async getCanModifyPrice() {
    this.canModifyPrice = (await firstValueFrom(this.salesService.apiSalesCanModifyPriceRetailerIdGet(parseInt(this.saleFormData.sellerData?.retailerId, 10)))).response ?? false;
  }
}
